import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINTS, FERRY_TRIP_API_URL, SERVER_URL } from 'Constants';
import saveAs from 'file-saver';
import { FerryBookingCommsDto } from 'Models/_HumanWritten/FerryBookingCommsDto';
import FerryEntity, { IFerryEntityAttributes } from 'Models/Entities/FerryEntity';
import FerryTripEntity, { CommunicationTabContactsCount } from 'Models/Entities/FerryTripEntity';
import FreightPriceRangeEntity, { IFreightPriceRangeEntityAttributes } from 'Models/Entities/FreightPriceRangeEntity';
import MeasurementEntity, { IMeasurementEntityAttributes } from 'Models/Entities/MeasurementEntity';
import { FerryTripRecurrencePayload } from './RecurrenceEntityService';
import RouteEntity, { IRouteEntityAttributes } from 'Models/Entities/RouteEntity';
import { CommunicationRecipientFilter } from 'Services/Api/_HumanWritten/CommunicationService';
import { getLowerCaseMessageByKey } from 'Stores/LanguageStore';
import { buildUrl } from 'Util/FetchUtils';
import { csvBlobFromText } from 'Util/_HumanWritten/FileDownloadUtils';
import { stringIsEmpty } from 'Util/TypeGuards';

export interface OverbookingSpaceResult {
	isOverbooking: boolean;
	cargoSpaceRemaining: number;
	cargoSpaceOverbookingAmount: number;
	passengerSpaceRemaining: number;
	passengerSpaceOverbookingAmount: number;
}

export async function generateVehicleOnlyManifest(ferryTrip: FerryTripEntity): Promise<void> {
	// one of the ways to download two csv files on one click
	const [vehicleManifest] = await Promise.all([
		axios.get(
			buildUrl(`${FERRY_TRIP_API_URL}/vehicle-manifest`, {
				Id: ferryTrip.id,
			}),
		),
	]);
	const vehicleBlobData = csvBlobFromText(vehicleManifest.data.toString());

	// extract the date and time to generate the file name
	const a = vehicleManifest.data.toString().split(',');
	const date = a[2].split('\r')[0].split(' ').join('-');
	const time = a[3].split('\r')[0];
	const vehicleFileName = `${getLowerCaseMessageByKey('vehicle__name__label', 'Vehicle')}Manifest(${date}_${time}).csv`;
	await saveAs(vehicleBlobData, vehicleFileName);
}

export async function generatePassengerOnlyManifest(ferryTrip: FerryTripEntity): Promise<void> {
	const [passengerManifest] = await Promise.all([
		axios.get(
			buildUrl(`${FERRY_TRIP_API_URL}/passenger-manifest`, {
				Id: ferryTrip.id,
			}),
		),
	]);
	const passengerBlobData = csvBlobFromText(passengerManifest.data.toString());

	// extract the date and time to generate the file name
	const a = passengerManifest.data.toString().split(',');
	const date = a[2].split('\r')[0].split(' ').join('-');
	const time = a[3].split('\r')[0];
	// eslint-disable-next-line no-useless-concat
	const passengerFileName = `${'PassengerManifest' + '('}${date}_${time}).csv`;
	await saveAs(passengerBlobData, passengerFileName);
}

export async function generateFreightOnlyManifest(ferryTrip: FerryTripEntity): Promise<void> {
	const [freightManifest] = await Promise.all([
		axios.get(
			buildUrl(`${FERRY_TRIP_API_URL}/freight-manifest`, {
				Id: ferryTrip.id,
			}),
		),
	]);
	const freightBlobData = csvBlobFromText(freightManifest.data.toString());

	// extract the date and time to generate the file name
	const a = freightManifest.data.toString().split(',');
	const date = a[2].split('\r')[0].split(' ').join('-');
	const time = a[3].split('\r')[0];
	// eslint-disable-next-line no-useless-concat
	const passengerFileName = `${'FreightManifest' + '('}${date}_${time}).csv`;
	await saveAs(freightBlobData, passengerFileName);
}

/**
 * Returns the count of distinct mobile numbers and email addresses that match the given recipient
 * filter on a ferry trip.
 * @param tripId ferry trip id to get the contact counts from.
 * @param filter the recipient filter (booking types, check-in status, waitlist inclusion).
 * @returns tripContactCount (smsCount and emailCount)
 */
export async function getTripContactCount(tripId: string, filter: CommunicationRecipientFilter) {
	try {
		const response = await axios.post<any, AxiosResponse<CommunicationTabContactsCount>>(
			buildUrl(`${FERRY_TRIP_API_URL}/ferry-trip-contact-count`, {
				tripId: tripId,
			}),
			filter,
		);

		return response.data;
	} catch (e) {
		console.error(e);
		return null;
	}
}

/** One recipient in the pre-send confirmation list. `source` is `Booking`, `Standby` or `Waitlist`. */
export interface TripCommunicationRecipient {
	source: 'Booking' | 'Standby' | 'Waitlist';
	name: string;
	/** Every booking this person holds on the trip; empty for standby and waitlist entries. */
	references: string[];
	email?: string | null;
	phone?: string | null;
}

/**
 * Itemises the same recipients getTripContactCount totals, for the confirmation shown before sending.
 * @param tripId ferry trip id to list recipients for.
 * @param filter the recipient filter (audiences, booking types, check-in status).
 */
export async function getTripContactPreview(tripId: string, filter: CommunicationRecipientFilter) {
	const response = await axios.post<any, AxiosResponse<TripCommunicationRecipient[]>>(
		buildUrl(`${FERRY_TRIP_API_URL}/ferry-trip-contact-preview`, {
			tripId: tripId,
		}),
		filter,
	);

	return response.data;
}

/**
 * The recipient counts across several trips, for the bulk Communications screen. Someone booked on
 * more than one of the trips is counted once, matching what the send does.
 */
export async function getBulkTripContactCount(ferryTripIds: string[], filter: CommunicationRecipientFilter) {
	try {
		const response = await axios.post<any, AxiosResponse<CommunicationTabContactsCount>>(
			`${FERRY_TRIP_API_URL}/bulk-trip-contact-count`,
			{ ferryTripIds, filter },
		);

		return response.data;
	} catch (e) {
		console.error(e);
		return null;
	}
}

/** The same recipients itemised, for the bulk pre-send confirmation. */
export async function getBulkTripContactPreview(ferryTripIds: string[], filter: CommunicationRecipientFilter) {
	const response = await axios.post<any, AxiosResponse<TripCommunicationRecipient[]>>(
		`${FERRY_TRIP_API_URL}/bulk-trip-contact-preview`,
		{ ferryTripIds, filter },
	);

	return response.data;
}

/**
 * Opens/Closes a ferry trip from online bookings by customers.
 * @param tripId ferry trip id to open/close.
 * @returns boolean of whether or not it was successful
 */
export async function toggleFerryTripOpenState(
	tripId: string,
	vehicleBookingsClosed: boolean,
	passengerBookingsClosed: boolean,
	freightBookingsClosed: boolean,
	eventBookingsClosed: boolean = false,
	preventAutomaticClosing: boolean = false,
	/** From check-in's menu, so the trip history shows the change as done at check-in. */
	inCheckIn: boolean = false,
) {
	try {
		const response = await axios.get<any, AxiosResponse<boolean>>(
			buildUrl(`${FERRY_TRIP_API_URL}/toggle-trip-open-state`, {
				inCheckIn: inCheckIn.toString(),
				tripId: tripId,
				vehicleBookingsClosed: vehicleBookingsClosed.toString(),
				passengerBookingsClosed: passengerBookingsClosed.toString(),
				freightBookingsClosed: freightBookingsClosed.toString(),
				eventBookingsClosed: eventBookingsClosed.toString(),
				preventAutomaticClosing: preventAutomaticClosing.toString(),
			}),
		);

		return response.data;
	} catch (e) {
		console.error(e);
		return null;
	}
}

export async function finaliseCheckIn(tripId: string, finalise = true) {
	try {
		const response = await axios.get<any, AxiosResponse<boolean>>(
			buildUrl(`${FERRY_TRIP_API_URL}/finalise-check-in`, {
				tripId: tripId,
				finaliseCheckIn: String(finalise),
			}),
		);

		return response.data;
	} catch (e) {
		console.error(e);
		return null;
	}
}

export function FetchSelectedFerryTrips(
	tripIds: string[],
	userId: string | null = null,
	bookingToEditId: string | null = null,
) {
	const res = axios.get(
		buildUrl(`${SERVER_URL}/api/entity/FerryTripEntity/fetch-selected-ferry-trips`, {
			tripIds: tripIds.join(','),
			userId: stringIsEmpty(userId) ? 'null' : userId,
			bookingToEditId: stringIsEmpty(bookingToEditId) ? 'null' : bookingToEditId,
		}),
	);
	return res;
}

export interface RecurringFerryTripDto {
	ferryTrip: FerryTripEntity;
	/** Create only — the server refuses a recurrence on an existing trip. */
	recurrence: FerryTripRecurrencePayload | null;
}

/** Saves a trip, or creates a recurring series when `recurrence` is set. Returns how many trips were saved. */
export async function AddRecurringFerryTrip(dto: RecurringFerryTripDto) {
	const res = await axios.post(`${FERRY_TRIP_API_URL}/add-ferry-trips`, {
		ferryTrip: dto.ferryTrip.toJSON({ prices: {}, tripStops: {} }),
		recurrence: dto.recurrence,
	});
	// A repeat answers how many trips it made; a single new trip answers its id; an edit answers nothing.
	if (typeof res.data === 'number') {
		return { count: res.data } as AddFerryTripsResult;
	}
	return { count: 1, tripId: typeof res.data === 'string' && res.data !== '' ? res.data : undefined } as AddFerryTripsResult;
}

export interface AddFerryTripsResult {
	count: number;
	tripId?: string;
}

export interface GetFerryTripElementsResponse {
	measurements: IMeasurementEntityAttributes[];
	freightPriceRanges: IFreightPriceRangeEntityAttributes[];
	routes: IRouteEntityAttributes[];
	ferries: IFerryEntityAttributes[];
}

interface FerryTripElements {
	measurements: MeasurementEntity[];
	freightPriceRanges: FreightPriceRangeEntity[];
	routes: RouteEntity[];
	ferries: FerryEntity[];
}

export async function getFerryTripElements(): Promise<FerryTripElements> {
	const res = await axios.get<GetFerryTripElementsResponse>(API_ENDPOINTS.TRIPS.ELEMENTS).then(x => x.data);
	return {
		measurements: res.measurements.map(x => new MeasurementEntity(x)),
		freightPriceRanges: res.freightPriceRanges
			.map(x => new FreightPriceRangeEntity(x))
			.sort((a, b) => {
				const aLabel = a.freightCategory?.label ?? a.freightHandling?.label;
				const bLabel = b.freightCategory?.label ?? b.freightHandling?.label;
				if (aLabel < bLabel) return -1;
				if (aLabel > bLabel) return 1;
				if (a.maxValue < b.maxValue) return -1;
				if (a.maxValue > b.maxValue) return 1;
				return 0;
			}),
		routes: res.routes.map(x => new RouteEntity(x)),
		ferries: res.ferries.map(x => new FerryEntity(x)),
	};
}

export async function getFerryBookingCommsByTrip(id: string) {
	if (!id) {
		return [];
	}

	const res = await axios.get<FerryBookingCommsDto[]>(API_ENDPOINTS.TRIPS.GET_TRIP_BOOKINGS(id)).then(x => x.data);

	return res;
}

interface FerryTripBulkUpdateFieldsDto {
	hidden?: boolean | null;
	configuration?: string | null;
	vehicleBookingsClosed?: boolean | null;
	passengerBookingsClosed?: boolean | null;
	freightBookingsClosed?: boolean | null;
	preventAutomaticClosing?: boolean | null;
	alterationRulesDisabled?: boolean | null;
	cancellationRulesDisabled?: boolean | null;
	/** Move the trips to this ferry; null capacityProfileId then takes its default. Needs ChangeTripCapacity. */
	ferryId?: string | null;
	/** Move the trips to this capacity profile. Needs ChangeTripCapacity. */
	capacityProfileId?: string | null;
}

export interface BulkUpdateFerryTripsRequestDto {
	ferryTripIds: string[];
	updates: FerryTripBulkUpdateFieldsDto;
}

export interface BulkUpdateFerryTripsResult {
	/** Whether the person who made the change was sent a copy of the trip capacity change email. */
	notifiedYou: boolean;
	/** `SystemEmails.tripCapacityChangeNotificationRecipients` addresses it went to, not counting the person themselves. */
	notifiedRecipients: number;
	/** Trips moved to another ferry or capacity profile. */
	changedTripIds: string[];
	/** Trips the ferry or profile change left alone, with why — blocked since the preview, say. */
	skipped: { ferryTripId: string; reason: string }[];
}

export async function bulkUpdateFerryTrips(request: BulkUpdateFerryTripsRequestDto): Promise<BulkUpdateFerryTripsResult> {
	const res = await axios.post<BulkUpdateFerryTripsResult | ''>(API_ENDPOINTS.TRIPS.POST_BULK_UPDATE, request);
	return res.data || { notifiedYou: false, notifiedRecipients: 0, changedTripIds: [], skipped: [] };
}

// --- Change ferry / capacity profile --------------------------------------

export interface BulkCapacityChangeRequest {
	ferryTripIds: string[];
	/** Null keeps each trip's own ferry. */
	ferryId?: string | null;
	/** Null takes the (new) ferry's default; only allowed with a ferry. */
	capacityProfileId?: string | null;
}

export type BulkCapacityChangeRowStatus = 'Ok' | 'Overbooked' | 'Blocked' | 'Unchanged';

export interface BulkCapacityChangeOverbooking {
	capacityType: string;
	booked: number;
	newCapacity: number;
	overBy: number;
}

export interface BulkCapacityChangeRow {
	ferryTripId: string;
	tripName?: string | null;
	routeName?: string | null;
	departureDateTime?: string | null;
	currentFerryId?: string | null;
	currentFerryName?: string | null;
	currentCapacityProfileId?: string | null;
	currentCapacityProfileName?: string | null;
	proposedFerryId: string;
	proposedFerryName?: string | null;
	proposedCapacityProfileId?: string | null;
	proposedCapacityProfileName?: string | null;
	status: BulkCapacityChangeRowStatus;
	blockReason?: string | null;
	overbookings: BulkCapacityChangeOverbooking[];
}

export interface BulkCapacityChangePreview {
	maxTrips: number;
	rows: BulkCapacityChangeRow[];
}

/**
 * What moving the trips to another ferry and/or capacity profile would do to each one, through the
 * real capacity engine. Nothing is saved; apply with {@link bulkUpdateFerryTrips}.
 */
export async function previewBulkCapacityChange(request: BulkCapacityChangeRequest) {
	const res = await axios.post<BulkCapacityChangePreview>(API_ENDPOINTS.TRIPS.POST_BULK_CAPACITY_CHANGE_PREVIEW, request);
	return res.data;
}

// --- Bulk trip selection (the scope of a bulk operation) -------------------
// These describe WHICH trips an operation targets. The same selection shape is
// reused by any bulk trip action (pricing, and future actions), independent of
// what the action then does. See tripSelectionScope.ts for the UI-side model
// and the mapper that produces this wire format.

/** How far forward the selection reaches. `range` is bounded; `all-future` is open-ended from `startDate`. */
export type TemporalMode = 'all-future' | 'range';
/** Whether the selection covers every route or a chosen subset. */
export type RouteScopeMode = 'all' | 'specific';

/**
 * Wire format identifying the set of trips a bulk operation targets.
 *
 * A route is directional in the model (departure → destination), so a corridor's
 * two directions are two separate routes. `routeIds` therefore expresses both the
 * route and the direction subset: pick both entries of a corridor for "both
 * directions", one for a single direction.
 */
export interface TripSelectionRequestDto {
	temporalMode: TemporalMode;
	startDate: string; // ISO date (yyyy-MM-dd); for `all-future` this is the "from" date (usually today)
	endDate?: string | null; // ISO date; null when temporalMode === 'all-future'
	routeMode: RouteScopeMode;
	routeIds: string[]; // selected directional routes; empty when routeMode === 'all'
}

// --- Bulk trip pricing ------------------------------------------------------

/** The sign of the price adjustment. `amount`/`percent` are always positive magnitudes. */
export type PriceAdjustmentDirection = 'increase' | 'decrease';

/**
 * How prices change. Mirrors the backend `PriceChangeInput`: `percent`/`rounding` apply
 * to percentage mode, `amount` to fixed mode; `set` mode carries its new amounts on the
 * target's pinned sets (`values`) and always sends a target. `target` narrows WHICH
 * stored prices take the change; null/absent applies it to every price on the matched
 * trips.
 */
export interface BulkTripPricingPriceChangeDto {
	mode: 'percentage' | 'fixed' | 'set';
	/** Delta modes only; set mode has no direction (it can raise and lower in one run). */
	direction?: PriceAdjustmentDirection;
	percent?: number;
	amount?: number;
	rounding?: string;
	target?: BulkTripPricingPriceTargetDto | null;
}

/**
 * Which stored prices the change applies to — mirrors the backend `PriceTargetInput`. The
 * target is a disjunction of rules, each rule a conjunction of optional constraints: a price
 * is written iff any rule matches it, and omitting a constraint means "all". Each targeting
 * level fills in one more constraint (domain → type/measurement → set), so all four levels
 * serialize through this one shape.
 */
export interface BulkTripPricingPriceTargetDto {
	rules: BulkTripPricingTargetRuleDto[];
	/** Level 4 only: the price sets referenced by rules' `setId`, pinned at enqueue. */
	sets?: Record<string, BulkTripPricingTargetSetDto>;
}

export interface BulkTripPricingTargetRuleDto {
	domain: 'passenger' | 'vehicle' | 'trailer';
	/** Passenger rules only: passenger type key (A–H). Absent = every type. */
	passengerTypeKey?: string;
	/** Vehicle/trailer rules only: the measurement the row is scoped to. Absent = every measurement. */
	measurementId?: string;
	/** Key into the target's `sets`. Absent = every trip in the run's selection. */
	setId?: string;
}

/**
 * A pinned price set a Level-4 rule targets: the trips that shared the set's prices at
 * preview time, plus the previewed amounts of the targeted lines (the backend skips —
 * and counts — any trip whose targeted price has drifted from these).
 */
export interface BulkTripPricingTargetSetDto {
	domain: 'passenger' | 'vehicle';
	tripIds: string[];
	/**
	 * Previewed amount per targeted line. Keys: flat passenger fare `{typeKey}`; per-leg
	 * passenger fare `{startStopId}:{typeKey}`; vehicle/trailer row
	 * `{startStopId|empty}:{measurementId}` — must mirror the backend's key builders exactly.
	 */
	expected?: Record<string, number | null>;
	/**
	 * Set mode only: the new amount per EDITED line, same key scheme as `expected`. A
	 * matched price with no entry here is left untouched (the operator didn't change it).
	 */
	values?: Record<string, number>;
}

/** Lifecycle of a bulk trip-pricing run (mirrors the backend BulkTripPricingRunStatus). */
export type BulkTripPricingRunStatusName =
	| 'Queued'
	| 'Running'
	| 'Completed'
	| 'CompletedWithErrors'
	| 'Failed';

/**
 * The pollable state of a bulk trip-pricing run: progress while running and, once terminal,
 * the outcome. The change is applied in the background, so the wizard enqueues a run and polls
 * this until a terminal status.
 */
export interface BulkTripPricingRunStatusDto {
	id: string;
	status: BulkTripPricingRunStatusName;
	/** Trips matched when the run was enqueued (the progress denominator). */
	matchedAtEnqueue: number;
	updated: number;
	skippedDeparted: number;
	/** Legacy: closed trips are repriced now, so this is 0 on any run started since. */
	skippedBookingsClosed: number;
	/** Trips where nothing matched the change's price target. */
	skippedNotTargeted: number;
	/** Trips skipped because a targeted price drifted from its previewed amount. */
	skippedPriceDrift: number;
	failed: number;
	/** Trips accounted for so far (updated + skipped + failed). */
	processed: number;
	startedAt: string | null;
	completedAt: string | null;
	/** Failure detail when the run ended Failed; null otherwise. */
	error: string | null;
	created: string;
}

/** True once a run has reached a terminal state and will no longer change. */
export function isTerminalBulkTripPricingRun(status: BulkTripPricingRunStatusName): boolean {
	return status === 'Completed'
		|| status === 'CompletedWithErrors'
		|| status === 'Failed';
}

/**
 * Enqueue a bulk price change over the matched trips. The request is the same flat selection
 * the preview uses plus the change, so both paths target the same set; the change applies to
 * the stored prices its `target` matches (every price when the target is absent). The change
 * is applied in the background — this returns the run id to poll via
 * {@link getBulkTripPricingRun}. A 409 means another run is already in progress.
 *
 * `tripIds` narrows the run to a hand-picked subset of the matched trips. When omitted, the
 * backend reprices the whole scope (the resolve-live-selection path, which stays correct for
 * an unbounded "all future" window). Pass it only when the operator has deselected some trips
 * — the bounded, explicit set they can see and chose.
 */
export async function executeBulkTripPricing(
	selection: TripSelectionRequestDto,
	change: BulkTripPricingPriceChangeDto,
	selectedTripIds?: string[],
): Promise<{ runId: string }> {
	// Only include selectedTripIds when narrowing to a subset — omitting it (rather than
	// sending null) preserves the backend's default empty list, i.e. "reprice the whole scope".
	const subset = selectedTripIds && selectedTripIds.length > 0
		? { selectedTripIds }
		: {};
	const res = await axios.post<{ runId: string }>(
		API_ENDPOINTS.PRICING.POST_BULK_TRIP_PRICING,
		{ ...selection, change, ...subset },
	);
	return res.data;
}

/**
 * Download a run's per-trip report: a CSV of every trip the run updated and each price it
 * changed (previous → new). The rows are persisted with the run's own writes, so the report
 * always matches what was actually applied. The filename comes from the server (stamped with
 * the run's completion time).
 */
export async function downloadBulkTripPricingRunReport(runId: string): Promise<void> {
	const res = await axios.get(API_ENDPOINTS.PRICING.GET_BULK_TRIP_PRICING_RUN_REPORT(runId), {
		responseType: 'blob',
	});
	// Content-Disposition: attachment; filename=bulk-price-change-report-....csv
	const fileName = /filename="?([^";]+)"?/.exec(res.headers['content-disposition'] ?? '')?.[1]
		?? 'bulk-price-change-report.csv';
	saveAs(new Blob([res.data], { type: 'text/csv;charset=utf-8' }), fileName);
}

/** Fetch the current state of a bulk trip-pricing run for polling. */
export async function getBulkTripPricingRun(runId: string): Promise<BulkTripPricingRunStatusDto> {
	const res = await axios.get<BulkTripPricingRunStatusDto>(
		API_ENDPOINTS.PRICING.GET_BULK_TRIP_PRICING_RUN(runId),
	);
	return res.data;
}

/**
 * The currently active (queued/running) run, if any — runs are serialized, so at most one
 * exists. Null when nothing is active (a 200 with a null body, not a 404, so probing isn't
 * an error path). Lets a freshly-loaded wizard re-attach to an in-flight run instead of
 * forgetting it exists.
 */
export async function getActiveBulkTripPricingRun(): Promise<BulkTripPricingRunStatusDto | null> {
	const res = await axios.get<BulkTripPricingRunStatusDto | null>(
		API_ENDPOINTS.PRICING.GET_ACTIVE_BULK_TRIP_PRICING_RUN,
	);
	// "Nothing active" arrives as a 204 with an empty body ('' after axios parsing), not a
	// JSON null — || (not ??) so the empty string also normalizes to null.
	return res.data || null;
}

// --- Bulk trip pricing preview ---------------------------------------------
// Resolves the trips a selection matches and their current prices, so the wizard's
// review step shows exactly what is about to change. Passenger fares come from the
// trip's flat fare columns keyed A–H; vehicle prices from its Price rows (by measurement).

/**
 * The pricing strategy name (the backend PricingStrategy enum name) a trip is priced by.
 * Only `Fixed` and `ConsecutiveStops` are live; the others are declared but unimplemented.
 */
export type PricingStrategyName = 'Fixed' | 'ConsecutiveStops' | 'OriginDestination' | 'ZoneBased';

/** A trip's passenger price (one per passenger type key A–H that the trip prices). */
export interface BulkTripPricingPassengerPriceDto {
	passengerTypeKey: string;
	amount: number | null;
}

/**
 * One leg's passenger price on a ConsecutiveStops trip: the price of boarding at `startStop`
 * for one passenger type. A journey's fare is the sum of these across the legs it travels.
 */
export interface BulkTripPricingPassengerSegmentPriceDto {
	/**
	 * The leg's start stop — the only stop a ConsecutiveStops row carries (a leg is
	 * "start stop → next consecutive stop"). The end stop is resolved from the route.
	 */
	startStopId: string;
	/** Start-stop name; a labelling fallback when the route lookup can't resolve the leg. */
	startStopName: string;
	startStopOrder: number;
	passengerTypeKey: string;
	amount: number | null;
}

/** A trip's vehicle/trailer price row (scoped to a measurement). */
export interface BulkTripPricingVehiclePriceDto {
	measurementId: string | null;
	trailerMeasurementId: string | null;
	/**
	 * The leg's start stop for per-leg (ConsecutiveStops) rows; null for whole-trip rows.
	 * Part of the row's identity — a ConsecutiveStops trip carries one row per
	 * (leg, measurement), so the measurement alone doesn't identify a row.
	 */
	startStopId: string | null;
	amount: number | null;
}

/** One matched trip and its current prices. */
export interface BulkTripPricingPreviewTripDto {
	tripId: string;
	routeId: string;
	departureDateTime: string; // ISO datetime
	departed: boolean;
	passengerBookingsClosed: boolean;
	/** The strategy the trip's passenger prices are computed by. */
	passengerPricingStrategy: PricingStrategyName;
	/**
	 * The strategy the trip's vehicle AND trailer prices are computed by. Chosen independently
	 * of the passenger strategy on the trip's pricing strategy profile, so it can't be inferred
	 * from {@link passengerPricingStrategy} — a trip can price passengers whole-trip and vehicles
	 * per leg. Trailers have no strategy of their own; the engine prices them with this one.
	 */
	vehiclePricingStrategy: PricingStrategyName;
	passengerPrices: BulkTripPricingPassengerPriceDto[];
	/** Per-leg passenger prices; populated only for ConsecutiveStops trips (else empty). */
	passengerSegmentPrices: BulkTripPricingPassengerSegmentPriceDto[];
	vehiclePrices: BulkTripPricingVehiclePriceDto[];
}

export async function previewBulkTripPricing(
	selection: TripSelectionRequestDto,
): Promise<BulkTripPricingPreviewTripDto[]> {
	const res = await axios.post<BulkTripPricingPreviewTripDto[]>(
		API_ENDPOINTS.PRICING.POST_BULK_TRIP_PRICING_PREVIEW,
		selection,
	);
	return res.data;
}
